/**
 * The auth context and its hook, kept apart from the provider component.
 *
 * Split because a file that exports both a component and other values loses React fast
 * refresh — every edit to the provider would reload the whole app rather than swapping
 * the component, which on a console with an open session means signing in again.
 */

import { createContext, useContext } from 'react';

import type { TenantBranding } from '@taxi/api-client';

import type { MfaChallenge } from './SecondFactor';

export type Branding = Pick<
  TenantBranding,
  'slug' | 'addresses' | 'displayName' | 'defaultCulture' | 'defaultCurrency' | 'timeZoneId' | 'primaryColor' | 'logoUrl' | 'supportPhone'
>;

export interface AuthState {
  /** Null until a company code has been entered and its branding fetched. */
  branding: Branding | null;
  signedIn: boolean;

  /**
   * The last session ended without anybody asking — the server refused to refresh it.
   *
   * What the sign-in screen says at the top. Cleared by the next sign-in and by *Sign out*,
   * because a person who pressed the button knows why they are looking at the form.
   */
  sessionExpired: boolean;

  /** True while the shell is still working out whether a session exists. */
  initialising: boolean;

  resolveTenant: (slug: string) => Promise<void>;

  /**
   * The password half of a sign-in.
   *
   * Returns a challenge when the account owes a second factor, and null when the session is
   * already live. A dispatcher or an administrator always owes one; a driver signing in from
   * this screen — which happens, on the simulator — does not.
   */
  signIn: (email: string, password: string) => Promise<MfaChallenge | null>;

  /**
   * Takes the session a finished second factor produced.
   *
   * Separate from the password half because the code is typed on a different screen, and
   * the thing that knows how to store a session is this provider rather than that screen.
   */
  completeSecondFactor: (session: { accessToken: string; refreshToken: string }) => void;

  /**
   * Redeems an invitation link into a password and a session.
   *
   * Sign-in and this differ in what is proved and in which direction. Sign-in proves a
   * password somebody already has; this proves they can read an address, and produces the
   * password afterwards. Both end in exactly the same place — a tenant session — which is
   * why it lives here rather than in the page that renders the form.
   */
  acceptInvitation: (
    slug: string,
    token: string,
    password: string,
  ) => Promise<MfaChallenge | null>;

  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }

  return context;
}
